import Comm from "./BaseComm";
import IComm, { ICommRequest } from "./IComm";
import { IRequestBuilder } from "../server/requests/RequestBuilder";
import { BaseModel } from "../model/BaseModel";
import RequestRegister from "../server/requests/RequestRegister";
import ErrorModel from "../model/ErrorModel";
import ConsoleUtil from "../utils/ConsoleUtil";

class CommManager {

    protected comm: IComm;
    protected modelUpdateQueue: BaseModel<any>[];
    private pingInterval: NodeJS.Timer;
    private timeInterval: number;

    constructor() {
        this.comm = new Comm();
    }

    /**
     * If the vendor has pingRequest, we need to send it along with every request in ordet to not break the session.
     * TODO: Find a better place for the overall ping logic.
     */
    public startPing(timeInterval:number = 10000) {
        if (!RequestRegister.has("pingRequest")) {
            return;
        }

        this.timeInterval = timeInterval;
        this.pingInterval = setInterval(() => {
            this.send(RequestRegister.get("pingRequest"),
                () => {},
                this,
                (data) => {
                    ConsoleUtil.log("ping error");
                    ErrorModel.update(data);
                }
            );
        }, this.timeInterval);
    }

    public stopPing() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
        }
    }

    public resetPing() {
        this.stopPing();
        this.startPing(this.timeInterval);
    }

    /**
     * Alow overriding of default comm module for edge casses (Max Win gaming for example)
     */
    public setComm(comm?: IComm) {
        this.comm = comm || new Comm();
    }

    public getComm(): IComm {
        return this.comm;
    }

    public registerModelUpdate(model: BaseModel<any>) {
        if (!this.modelUpdateQueue) {
            this.modelUpdateQueue = [];
        }
        if (model) {
            this.modelUpdateQueue.push(model);
        }
    }

    async send(request: IRequestBuilder, callback: (...args: any[]) => void, callbackScope: any, errorCallback?: (...args: any[]) => void, POSTWithCredentials = false, enqueueRequest = true) {
        callback = callback == undefined ? this.emptyFunc() : callback.bind(callbackScope);
        const combinedCallback = (...args: any[]) => {
            this.precallback(...args);
            callback(...args);
        }

        errorCallback = errorCallback == undefined ? this.emptyFunc() : errorCallback.bind(callbackScope);

        let result: boolean;
        if (!request.path) {
            result = await new Promise<boolean>((res) => {
                callback();
                res(true);
            })
        } else {
            const commRequest: ICommRequest = {
                builder: request,
                callback: combinedCallback,
                callbackScope: callbackScope,
                errorCallback: errorCallback,
                withCredentials: POSTWithCredentials,
                enqueueRequest: enqueueRequest,
                retryCount: 0
            };

            result = await this.comm.send(commRequest);
        }

        this.resetPing();
        return result;
    }

    precallback(...args: any[]) {
        if (this.modelUpdateQueue) {
            this.modelUpdateQueue.forEach(model => {
                model.update(...args);
            })
        }
    }

    emptyFunc() {
    }

    /**
     * Stops the communication with the server.
     * Current ongoing requests won't be intercepted.
     *
     * @memberof CommManager
     */
    disableComm(stopPing: boolean = true) {
        ConsoleUtil.warn("COMMUNICATION WITH SERVER HAS BEEN STOPPED!");
        this.comm.enabled = false;
        if (stopPing) {
            this.stopPing();
        }
    }

    /**
     * Enables the communication with the server.
     *
     * @memberof CommManager
     */
    enableComm(startPing: boolean = true) {
        this.comm.enabled = true;
        if (startPing) {
            this.startPing(this.timeInterval);
        }
    }

    /**
     * Gets a copy of the requests queue.
     *
     * @type {RequestData[]}
     * @memberof IComm
     */
    get requestsQueue() {
        return this.comm.requestsQueue;
    }
}

export default new CommManager();
